import React, { useState, useMemo, useEffect } from "react";
import { BookingRecord, PaginationMeta } from "../../types";
import {
  getFinanceSummary,
  FinanceSummary,
} from "../../services/api/admin/finance";
import { getOrders } from "../../services/api/admin/orders";
import {
  Search,
  Wallet,
  TrendingUp,
  ShieldCheck,
  Download,
  Calendar,
  FileText,
  Activity,
  Tag,
  Users,
  ArrowDownCircle,
  Percent,
  Banknote,
  Loader2,
} from "lucide-react";
import Pagination from "../Pagination";

interface FinanceReportProps {
  onSelectBooking: (booking: BookingRecord) => void;
}

export const FinanceReport: React.FC<FinanceReportProps> = ({
  onSelectBooking,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
    total: 0,
    perPage: 10,
    currentPage: 1,
    lastPage: 1,
    firstPage: 1,
  });
  const limit = 10;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [financeRes, ordersRes] = await Promise.all([
        getFinanceSummary(),
        getOrders({
          page,
          limit,
          search: searchQuery || undefined,
          status: "paid",
          startDate: dateFilter || undefined,
          endDate: dateFilter || undefined,
        }),
      ]);

      setSummary(financeRes.summary);
      setBookings(ordersRes.data);
      setPaginationMeta({ ...ordersRes.meta, currentPage: page });
    } catch (error) {
      console.error("Failed to fetch finance report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timer);
  }, [page, dateFilter, searchQuery]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm">
          <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-1">
            Omzet Kotor
          </p>
          <h3 className="text-base font-black text-gray-900">
            Rp {(summary?.total_gross ?? 0).toLocaleString("id-ID")}
          </h3>
        </div>
        <div className="bg-[#ECFDF5] p-4 rounded-3xl border border-emerald-100">
          <p className="text-[9px] text-emerald-500 font-black uppercase tracking-widest mb-1">
            Total Dibayar
          </p>
          <h3 className="text-lg font-black text-emerald-700">
            Rp {(summary?.total_revenue ?? 0).toLocaleString("id-ID")}
          </h3>
        </div>
        <div className="bg-[#FFF5F5] p-4 rounded-3xl border border-rose-100">
          <p className="text-[9px] text-rose-400 font-black uppercase tracking-widest mb-1">
            Potongan & Fee
          </p>
          <h3 className="text-base font-black text-rose-600">
            -Rp{" "}
            {(summary
              ? summary.total_discount +
                summary.total_commission +
                summary.total_service_fee
              : 0
            ).toLocaleString("id-ID")}
          </h3>
        </div>
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 p-4 rounded-3xl text-white shadow-lg">
          <p className="text-[9px] text-indigo-100 font-black uppercase tracking-widest mb-1 opacity-80">
            Laba Bersih
          </p>
          <h3 className="text-lg font-black">
            Rp {(summary?.total_net_income ?? 0).toLocaleString("id-ID")}
          </h3>
        </div>
      </div>

      <div className="bg-white p-4 rounded-3xl border border-gray-200 flex flex-col lg:flex-row gap-4 justify-between items-center shadow-sm">
        <div className="relative w-full lg:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari ID, Nama, Voucher..."
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-giz-primary"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-giz-primary"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPage(1);
            }}
          />
          {dateFilter && (
            <button
              onClick={() => {
                setDateFilter("");
                setPage(1);
              }}
              className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-xs font-bold transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-[2.5rem] overflow-hidden shadow-sm relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-30 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-giz-primary animate-spin" />
          </div>
        )}
        <div className="overflow-auto scrollbar-thin">
          <table className="w-full text-sm text-left border-separate border-spacing-0">
            <thead className="bg-[#1E293B] text-white font-black uppercase text-[9px] tracking-[0.2em] sticky top-0 z-20">
              <tr>
                <th className="px-8 py-6">Transaksi</th>
                <th className="px-6 py-6 text-center">Pembayaran</th>
                <th className="px-6 py-6">Potongan & Affiliate</th>
                <th className="px-8 py-6 text-right">Profit Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bookings.map((item) => {
                const grossPrice = item.grossPrice || 0;
                const voucherDiscount = item.voucherDiscount || 0;
                const bulkDiscount = item.bulkDiscount || 0;
                const affiliateFee = item.affiliateFee || 0;
                const netIncome = item.netIncome || 0;

                return (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => onSelectBooking(item)}
                  >
                    <td className="px-8 py-5">
                      <div className="font-mono font-black text-gray-900 tracking-wider mb-1">
                        {item.code}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase truncate max-w-[150px]">
                        {item.customerName}
                      </div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <div className="text-[9px] text-gray-400 font-bold mb-1">
                        Gross: Rp {grossPrice.toLocaleString()}
                      </div>
                      <div className="font-black text-emerald-600 text-sm">
                        Rp {item.totalPrice.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="space-y-1.5">
                        {(voucherDiscount > 0 || bulkDiscount > 0) && (
                          <div className="flex justify-between gap-4 text-[9px] font-black text-rose-500 uppercase tracking-tight">
                            <span>
                              Disc{" "}
                              {item.voucherCode
                                ? `(${item.voucherCode})`
                                : `(${item.category})`}
                            </span>
                            <span>
                              -Rp{" "}
                              {(
                                voucherDiscount + bulkDiscount
                              ).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {affiliateFee > 0 && (
                          <div className="flex justify-between gap-4 text-[9px] font-black text-orange-500 uppercase tracking-tight">
                            <span>Affiliate ({item.affiliateName})</span>
                            <span>-Rp {affiliateFee.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="font-black text-indigo-900 text-base">
                        Rp {netIncome.toLocaleString("id-ID")}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <Pagination meta={paginationMeta} onPageChange={handlePageChange} />
        </div>
      </div>
    </div>
  );
};
